import React, { useState } from 'react';
import { Download, Sparkles } from 'lucide-react';
import { TaskyAlert, TaskyButton, TaskyCard } from '../tasky';
import { cn } from '../../lib/utils';
import type { EasyVideoProject } from '../../services/api/easyVideoService';

export interface EasyVideoResultProps {
  project: EasyVideoProject;
  /** 同じ商品・使う場所で新しく作る（Q3 から） */
  onAnotherOne: () => void;
  anotherOneLoading?: boolean;
  error?: string | null;
}

const EasyVideoResult: React.FC<EasyVideoResultProps> = ({ project, onAnotherOne, anotherOneLoading, error }) => {
  const video = project.final_video;
  const vertical = project.aspect_ratio === '9:16';
  const downloadUrl = video?.download_url || video?.url || null;
  const [downloading, setDownloading] = useState(false);

  // CDN の URL は Content-Disposition が付かず、別オリジンの <a download> も効かないため、
  // blob にしてから保存する。取れなければ新しいタブで開く（そこから保存できる）
  const handleDownload = async () => {
    if (!downloadUrl) return;
    setDownloading(true);
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${project.product?.name ?? 'video'}-${project.id.slice(0, 8)}.mp4`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // クリック直後に破棄すると保存が始まらないブラウザがあるので少し待つ
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      window.open(downloadUrl, '_blank', 'noopener');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 px-4 py-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-dark">動画ができました</h2>
        <p className="text-sm text-gray-600">
          {project.product?.name}
          {project.appeal?.label && ` ／ ${project.appeal.label}`}
        </p>
      </div>

      <TaskyCard variant="bordered" padding="sm">
        {video?.url ? (
          <video
            src={video.url}
            controls
            playsInline
            className={cn('mx-auto w-full rounded-tasky-sm bg-dark', vertical ? 'max-w-xs aspect-[9/16]' : 'aspect-video')}
            data-testid="easy-video-final"
          />
        ) : (
          <TaskyAlert tone="warning">動画のファイルを読み込めませんでした。少し待ってから再読み込みしてください。</TaskyAlert>
        )}
      </TaskyCard>

      <p className="text-sm text-gray-600" data-testid="easy-video-consumed">
        使ったクレジット: 約{project.credits.consumed.toLocaleString()} cr
      </p>

      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div className="flex flex-col gap-2 sm:flex-row">
        {downloadUrl && (
          <TaskyButton className="flex-1" isLoading={downloading} onClick={handleDownload}>
            <Download />
            ダウンロード
          </TaskyButton>
        )}
        <TaskyButton variant="secondary" className="flex-1" isLoading={anotherOneLoading} onClick={onAnotherOne}>
          <Sparkles />
          別の伝えたいことでもう1本
        </TaskyButton>
      </div>
    </div>
  );
};

export default EasyVideoResult;
